import { useState, useEffect, useRef } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Megaphone,
  Users,
  CalendarDays,
  CheckSquare,
  BarChart3,
  Activity,
  UserCog,
  Settings,
  SlidersHorizontal,
  Wallet,
  Building2,
  Sparkles,
  LogOut,
  X,
  ChevronUp,
  ShieldCheck,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import NotificationCenter from '@/components/NotificationCenter'
import ThemeSwitcher from '@/components/ThemeSwitcher'
import type { FeaturePermissionKey } from '@/types'
import RunnaMark from '@/components/RunnaMark'

type NavItem = {
  to: string
  icon: typeof LayoutDashboard
  label: string
  permission: FeaturePermissionKey
}

const ALL_NAV_ITEMS: NavItem[] = [
  { to: '/',              icon: LayoutDashboard, label: 'Dashboard',                 permission: 'ver_dashboard' },
  { to: '/aprovacoes',    icon: CheckSquare,     label: 'Aprovações',                permission: 'ver_aprovacoes' },
  { to: '/campanhas',     icon: Megaphone,       label: 'Campanhas',                 permission: 'ver_campanhas' },
  { to: '/influenciadores', icon: Users,         label: 'Influenciadores',           permission: 'ver_influenciadores' },
  { to: '/clientes',      icon: Building2,       label: 'Clientes',                  permission: 'ver_clientes' },
  { to: '/curadoria',     icon: Sparkles,        label: 'Curadoria',                 permission: 'ver_curadoria' },
  { to: '/cronograma',    icon: CalendarDays,    label: 'Cronograma',                permission: 'ver_cronograma' },
  { to: '/relatorios',    icon: BarChart3,       label: 'Relatórios',                permission: 'ver_relatorios' },
  { to: '/performance',   icon: Activity,        label: 'Performance',               permission: 'ver_performance' },
  { to: '/pagamentos',    icon: Wallet,          label: 'Pagamentos',                permission: 'ver_pagamentos_aba' },
  { to: '/equipe',        icon: UserCog,         label: 'Pessoas e Funcionalidades', permission: 'ver_equipe' },
  { to: '/configuracoes/agencia', icon: SlidersHorizontal, label: 'Configurações da agência', permission: 'ver_configuracoes_agencia' },
  // /configuracoes (preferências pessoais) vive no menu do avatar — padrão da
  // indústria pra preferências individuais (Linear, Notion, Slack, GitHub).
]

function getNavItems(profilePermissions: Partial<Record<FeaturePermissionKey, boolean>> | undefined) {
  const allowedKeys = profilePermissions
    ? new Set(Object.entries(profilePermissions).filter(([, v]) => v === true).map(([k]) => k))
    : new Set<string>()
  return ALL_NAV_ITEMS.filter(item => allowedKeys.has(item.permission))
}

interface SidebarProps {
  mobileOpen: boolean
  onClose: () => void
}

export default function Sidebar({ mobileOpen, onClose }: SidebarProps) {
  const {
    signOut, persona, displayName, profile, realProfile,
    isPlatformAdmin, impersonating,
  } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const navItems = getNavItems(profile?.permissions)

  useEffect(() => {
    if (!userMenuOpen) return
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [userMenuOpen])

  function goToConfiguracoes() {
    setUserMenuOpen(false)
    navigate('/configuracoes')
  }

  function goToPlatformAdmin() {
    setUserMenuOpen(false)
    const host = window.location.hostname
    const adminOrigin =
      host === 'runnaflow.com' || host === 'www.runnaflow.com'
        ? 'https://admin.runnaflow.com'
      : host === 'runnaflow-dev.runnaflow.com'
        ? 'https://admin-dev.runnaflow.com'
      : null
    if (adminOrigin) {
      window.location.href = `${adminOrigin}/`
    } else {
      // local dev / preview URL sem admin domain pareado → client-side route
      navigate('/platform-admin')
    }
  }

  const isOnPortal = location.pathname.startsWith('/portal')

  // Display: em impersonação, mostra nome do target + subtítulo com email do admin
  const avatarLabel = profile?.user.name
    ? profile.user.name.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase()
    : persona.initial
  const subtitle = impersonating
    ? `impersonado por ${realProfile?.user.name ?? 'admin'}`
    : profile?.team?.role
      ? (profile.team.role === 'admin' ? 'Admin' : profile.team.role === 'manager' ? 'Gestor' : profile.team.role === 'coordinator' ? 'Coordenador' : 'Assistente')
      : profile?.user.user_type === 'client_user'
        ? 'Cliente'
        : persona.roleLabel

  return (
    <aside className={`fixed left-0 top-0 bottom-0 w-60 bg-sidebar flex flex-col z-50 transition-transform duration-300 border-r border-card-border ${
      mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
    }`} role="navigation" aria-label="Menu principal">
      <div className="flex items-center justify-between px-5 py-[18px] border-b border-card-border">
        <div className="flex items-center gap-3">
          {profile?.user.user_type === 'client_user' && profile.client?.client_ref?.branding?.logo_url ? (
            <img
              src={profile.client.client_ref.branding.logo_url}
              alt={profile.client.client_ref.legal_name}
              className="w-9 h-9 shrink-0 rounded-md object-contain bg-card-hover"
            />
          ) : (
            <RunnaMark className="w-9 h-9 shrink-0" />
          )}
          <div className="flex flex-col">
            <span className="text-bright font-bold text-[15px] tracking-tight font-heading leading-tight">
              {profile?.user.user_type === 'client_user' && profile.client?.client_ref?.legal_name
                ? profile.client.client_ref.legal_name.split(/\s+/).slice(0, 2).join(' ')
                : 'Runna'}
            </span>
            <span className="text-dim text-[9.5px] font-medium tracking-[0.12em] uppercase mt-[1px]">
              {profile?.user.user_type === 'client_user' ? 'Portal do cliente' : (profile?.tenant?.name ?? 'Runnaflow')}
            </span>
          </div>
        </div>
        <button onClick={onClose} className="lg:hidden text-dim hover:text-text cursor-pointer">
          <X className="w-5 h-5" />
        </button>
      </div>

      <nav className="flex-1 py-3 overflow-y-auto">
        {navItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onClose}
            className={({ isActive }) =>
              `relative flex items-center gap-3 mx-2.5 px-3 py-[9px] text-[13.5px] font-medium rounded-lg transition-colors ${
                isActive ? 'text-primary' : 'text-text hover:text-bright hover:bg-sidebar-hover'
              }`
            }
            style={({ isActive }) => isActive
              ? { background: 'var(--color-primary-soft)', boxShadow: 'inset 0 0 0 1px var(--color-primary-line)' }
              : undefined}
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <span className="absolute left-0 top-[5px] bottom-[5px] w-[3px] rounded-r-full"
                    style={{ background: 'var(--color-primary)' }} aria-hidden />
                )}
                <Icon className="w-[17px] h-[17px]" strokeWidth={isActive ? 2.25 : 1.75} />
                {label}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-card-border p-4 relative" ref={menuRef}>
        {userMenuOpen && (
          <div className="absolute bottom-full left-4 right-4 mb-2 bg-card border border-card-border rounded-xl shadow-2xl overflow-hidden z-50">
            {/* Platform admin: link pro painel admin */}
            {isPlatformAdmin && (
              <button
                onClick={goToPlatformAdmin}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs hover:bg-card-hover transition-colors cursor-pointer"
                style={{ color: 'var(--color-primary)' }}
              >
                <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
                <span className="flex-1 text-left font-semibold">Painel Platform Admin</span>
              </button>
            )}

            {/* Configurações pessoais — sempre disponível pra qualquer user
                autenticado (acessa preferências individuais: tema, notificações,
                privacidade, sobre). Padrão Linear/Notion/Slack/GitHub. */}
            {!isOnPortal && (
              <button
                onClick={goToConfiguracoes}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs hover:bg-card-hover transition-colors cursor-pointer text-text hover:text-bright"
              >
                <Settings className="w-3.5 h-3.5 shrink-0" />
                <span className="flex-1 text-left">Configurações</span>
              </button>
            )}

            <button
              onClick={() => { setUserMenuOpen(false); signOut() }}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs hover:bg-card-hover transition-colors cursor-pointer text-dim hover:text-bright"
            >
              <LogOut className="w-3.5 h-3.5 shrink-0" />
              <span className="flex-1 text-left">Sair</span>
            </button>
          </div>
        )}

        <div className="flex items-center gap-3 mb-3">
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="w-8 h-8 rounded-full bg-card-hover flex items-center justify-center text-xs font-bold cursor-pointer hover:ring-2 hover:ring-card-border transition-all shrink-0"
            style={{ color: 'var(--color-bright)' }}
            title="Menu do usuário"
          >
            {avatarLabel}
          </button>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex-1 min-w-0 text-left cursor-pointer group"
          >
            <p className="text-bright text-xs font-medium truncate flex items-center gap-1.5">
              {displayName}
              <ChevronUp className={`w-3 h-3 text-dim transition-transform ${userMenuOpen ? '' : 'rotate-180'}`} />
            </p>
            <p className="text-dim text-[11px] truncate">{subtitle}</p>
          </button>
          <ThemeSwitcher />
          <NotificationCenter />
        </div>
      </div>
    </aside>
  )
}
